import React from 'react';
import {
  Button,
  Typography,
  Chip,
  Box,
  Divider,
} from '@mui/material';
import { NotificationsActive as SystemIcon } from '@mui/icons-material';
import { Notification } from '../../types/notification';
import { UserAvatar } from '../common/UserAvatar';
import { BaseDialog } from '../common/BaseDialog';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';
import { useNavigate } from 'react-router-dom';
import { resolvePushTarget } from '../../utils/resolve-push-target';
import { useMarkNotificationRead } from '../../hooks/useMarkNotificationRead';

interface NotificationDetailModalProps {
  open: boolean;
  onClose: () => void;
  notification: Notification | null;
}

const LEVEL_LABELS: Record<'info' | 'warning' | 'danger', string> = {
  info: 'Інформація',
  warning: 'Попередження',
  danger: 'Небезпека',
};

const getChipColor = (level: 'info' | 'warning' | 'danger') => {
  switch (level) {
    case 'info':    return 'info';
    case 'warning': return 'warning';
    case 'danger':  return 'error';
    default:        return 'default';
  }
};

export const NotificationDetailModal: React.FC<NotificationDetailModalProps> = ({
  open,
  onClose,
  notification,
}) => {
  const navigate = useNavigate();
  const markAsRead = useMarkNotificationRead();
  if (!notification) return null;

  const isExpert = notification.source === 'expert';
  const target = resolvePushTarget(notification.pushData);

  const handleGoToTarget = () => {
    if (!target) return;
    markAsRead(notification);
    navigate(target);
    onClose();
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title="Сповіщення"
      iDoc={ModalIndices.NOTIFICATION_DETAIL}
      contentSx={{ p: 0 }}
      actions={
        target ? (
          <Button variant="contained" onClick={handleGoToTarget}>
            Перейти
          </Button>
        ) : undefined
      }
    >
      {/* Sender header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: isExpert ? 'primary.main' : 'action.hover',
        }}
      >
        {isExpert ? (
          <UserAvatar
            name={notification.senderName ?? undefined}
            sx={{ width: 40, height: 40, fontSize: '1rem', bgcolor: 'primary.dark' }}
          />
        ) : (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'action.selected',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SystemIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          </Box>
        )}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ color: isExpert ? 'primary.contrastText' : 'text.primary', lineHeight: 1.2 }}
          >
            {isExpert ? notification.senderName : 'Платформа'}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: isExpert ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}
          >
            {isExpert ? 'Персональне повідомлення' : 'Системне сповіщення'}
          </Typography>
        </Box>
        <Chip
          label={LEVEL_LABELS[notification.level]}
          color={getChipColor(notification.level)}
          size="small"
          sx={{ ml: 'auto' }}
        />
      </Box>

      <Divider />

      {/* Body */}
      <Box sx={{ px: 3, py: 2.5 }}>
        <Typography variant="body1" sx={{ mb: 2.5, lineHeight: 1.6 }}>
          {notification.message}
        </Typography>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Дата</Typography>
            <Typography variant="body2">
              {new Date(notification.createdAt).toLocaleString('uk-UA')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Статус</Typography>
            <Typography variant="body2">
              {notification.isRead ? 'Прочитано' : 'Нове'}
            </Typography>
          </Box>
        </Box>

        {/* Dev: raw pushData для діагностики routing'у і контракту */}
        {notification.pushData && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Корисне навантаження (dev)
            </Typography>
            <Box
              component="pre"
              sx={{
                mt: 0.5,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'action.hover',
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                m: 0,
              }}
            >
              {JSON.stringify(notification.pushData, null, 2)}
            </Box>
          </Box>
        )}
      </Box>
    </BaseDialog>
  );
};
